import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { ApiData, HistoryEntry, ParsedHistoryEntry } from '../types/api';

export const useApiData = () => {
  const [data, setData] = useState<ApiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    const response = await apiService.getAllData();
    
    if (response.success && response.data) {
      setData(response.data);
    } else {
      setError(response.message || 'Erro ao carregar dados');
    }
    
    setLoading(false);
  };

  const silentRefetch = async () => {
    setError(null);
    
    const response = await apiService.getAllData();
    
    if (response.success && response.data) {
      setData(response.data);
    } else {
      setError(response.message || 'Erro ao carregar dados');
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  const updateColumnValue = async (columnId: number, industryId: number, value: string) => {
    const response = await apiService.updateColumnValues(columnId, industryId, value);
    
    if (response.success) {
      // Silent refresh to preserve scroll position
      await silentRefetch();
      return { success: true };
    } else {
      return { 
        success: false, 
        message: response.message || 'Erro ao atualizar valores' 
      };
    }
  };

  const updateGoLive = async (industryId: number, numeroGoLive: string) => {
    const response = await apiService.updateIndustryGoLive(industryId, numeroGoLive);
    
    if (response.success) {
      // Silent refresh to preserve scroll position
      await silentRefetch();
      return { success: true };
    } else {
      return { 
        success: false, 
        message: response.message || 'Erro ao atualizar número Go Live' 
      };
    }
  };

  const createIndustry = async (industryData: {
    name: string;
    numeroGoLive: string;
    fields: Record<string, string>;
  }) => {
    const response = await apiService.createIndustry(industryData);
    
    if (response.success) {
      // Refresh data after creating new industry
      await silentRefetch();
      return { success: true };
    } else {
      return { 
        success: false, 
        message: response.message || 'Erro ao criar indústria' 
      };
    }
  };

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    updateColumnValue,
    updateGoLive,
    createIndustry,
  };
};

export const useHistoryData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseHistoryData = (entries: HistoryEntry[], data?: ApiData): ParsedHistoryEntry[] => {
    return entries.map(entry => {
      try {
        const parsedData = JSON.parse(entry.data);
        const result: ParsedHistoryEntry = {
          value: parsedData.value || '',
          columnId: parsedData.columnId?.toString() || '',
          industryId: parsedData.industryId || 0,
          created_at: entry.created_at,
          user: entry.user,
        };

        // Try to find column and industry names from the data
        if (data) {
          Object.entries(data).forEach(([industryName, industryData]) => {
            if (industryData.industryId === result.industryId) {
              result.industryName = industryName;
              
              Object.entries(industryData.sections).forEach(([sectionName, section]) => {
                Object.entries(section.columns).forEach(([columnName, column]) => {
                  if (column.columnId.toString() === result.columnId) {
                    result.columnName = columnName;
                  }
                });
              });
            }
          });
        }

        return result;
      } catch (error) {
        console.error('Error parsing history entry:', error);
        return {
          value: '',
          columnId: '',
          industryId: 0,
          created_at: entry.created_at,
          user: entry.user,
        };
      }
    });
  };

  const getGlobalHistory = async (data?: ApiData): Promise<ParsedHistoryEntry[]> => {
    setLoading(true);
    setError(null);
    
    const response = await apiService.getGlobalHistory();
    
    if (response.success && response.data) {
      const parsedEntries = parseHistoryData(response.data, data);
      setLoading(false);
      return parsedEntries;
    } else {
      setError(response.message || 'Erro ao carregar histórico');
      setLoading(false);
      return [];
    }
  };


  const getCellHistory = async (columnId: number, industryId: number, data?: ApiData): Promise<ParsedHistoryEntry[]> => {
    setLoading(true);
    setError(null);
    
    const response = await apiService.getCellHistory(columnId, industryId);
    
    if (response.success && response.data) {
      const parsedEntries = parseHistoryData(response.data, data);
      setLoading(false);
      return parsedEntries;
    } else {
      setError(response.message || 'Erro ao carregar histórico da célula');
      setLoading(false);
      return [];
    }
  };

  return {
    loading,
    error,
    getGlobalHistory,
    getCellHistory,
    parseHistoryData,
  };
};